import { useCallback } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import useProjectId from '../../customHooks/useProjectId'
import { useAccessToken } from '../useAccessToken'
import { useHandleError } from '../useHandleError'
import { makeResultFetch, FetchResponse } from '../genericFetch'
import { apiRequestParams } from '../fetchOptions'

export const useCompleteProjectForecast = (reportGuid: string | null) => {
  const getAccessToken = useAccessToken()
  const handleError = useHandleError()
  const projectId = useProjectId()
  const queryClient = useQueryClient()

  const mutationFunction = useCallback(async () => {
    if (!reportGuid) {
      throw new Error('Report GUID is required')
    }

    const accessToken = await getAccessToken()

    if (!accessToken) throw new Error('Access token is undefined')

    const response = await makeResultFetch<boolean>(
      `/reports/${projectId}/projectForecast/${reportGuid}/complete`,
      apiRequestParams(accessToken, 'POST')
    ).catch(() => {
      throw new Error(
        'Det gick inte att ansluta till servern. Kontrollera din internetuppkoppling.'
      )
    })

    if (!response.success) {
      throw new Error(response.message)
    }

    return response
  }, [getAccessToken, projectId, reportGuid])

  return useMutation<FetchResponse<boolean>, Error>({
    mutationFn: mutationFunction,
    networkMode: 'always',
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [projectId, 'periodReportSnapshots'],
      })
    },
    onError: handleError,
  })
}
